import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useSearch } from "wouter";
import { SETTINGS_DESTINATIONS } from "@shared/settingsDestinations";
import { customerBillingSetupHref } from "@shared/billingNavigation";
import { DEFAULT_SECTION_ID, findSection } from "./registry";
import {
  websiteEditorControlDestination,
  websiteEditorDestination,
  type WebsiteEditorSection,
} from "@/lib/websiteEditorDestinations";

type SettingsHubContextValue = {
  open: boolean;
  activeSection: string;
  // The tab selected within the active section (for tabbed sections). null means
  // "let the hub resolve the first visible tab". Reset to null whenever the
  // section changes so a fresh section always opens on its first tab.
  activeTab: string | null;
  openSettings: (sectionId?: string, tabId?: string) => void;
  closeSettings: () => void;
  setActiveSection: (sectionId: string) => void;
  setActiveTab: (tabId: string) => void;
  // A section/tab with unsaved, easily-lost state (e.g. a staged CSV import) can
  // register a warning message here. While set, the hub routes every close /
  // section-switch / tab-switch attempt through a confirmation dialog and
  // installs a beforeunload guard. Clearing it (null) removes the guard.
  pendingGuardMessage: string | null;
  setPendingGuard: (message: string | null) => void;
};

const SettingsHubContext = createContext<SettingsHubContextValue | null>(null);

// Tolerant variant for components that also render outside the dashboard
// shell (e.g. in unit tests without the provider): returns null instead of
// throwing. Callers must no-op when null.
export function useSettingsHubMaybe(): SettingsHubContextValue | null {
  return useContext(SettingsHubContext);
}

export function useSettingsHub(): SettingsHubContextValue {
  const ctx = useContext(SettingsHubContext);
  if (!ctx) {
    throw new Error("useSettingsHub must be used within a SettingsHubProvider");
  }
  return ctx;
}

export function SettingsHubProvider({ children }: { children: React.ReactNode }) {
  const searchString = useSearch();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSectionState] = useState<string>(DEFAULT_SECTION_ID);
  const [activeTab, setActiveTabState] = useState<string | null>(null);
  const [pendingGuardMessage, setPendingGuard] = useState<string | null>(null);

  const openSettings = useCallback((sectionId?: string, tabId?: string) => {
    if (sectionId === "client-billing" || (sectionId === "messaging" && (!tabId || tabId === "client-billing"))) {
      setOpen(false);
      navigate(customerBillingSetupHref(window.location.search));
      return;
    }
    if (sectionId === "business-info" && (tabId === "logo" || tabId === "accent")) {
      setOpen(false);
      navigate(websiteEditorDestination("site-setup"));
      return;
    }
    if (sectionId === "website" && tabId === "domain") {
      setActiveSectionState(SETTINGS_DESTINATIONS.domainEmail.sectionId);
      setActiveTabState(null);
      setOpen(true);
      return;
    }
    if (sectionId === "business-info" && (tabId === "hours" || tabId === "contact" || tabId === "social")) {
      setOpen(false);
      navigate(websiteEditorDestination("footer"));
      return;
    }
    if (sectionId === "website" || sectionId === "public-page") {
      const sectionByTab: Record<string, WebsiteEditorSection> = {
        branding: "set-up",
        colors: "set-up",
        effects: "footer",
        seo: "set-up",
        "footer-branding": "footer",
        hero: "hero",
        privacy: "pages",
        terms: "pages",
        testimonial: "pages",
        pages: "pages",
        hours: "footer",
        contact: "footer",
        social: "footer",
      };
      setOpen(false);
      const control = ({
        branding: "colors",
        colors: "colors",
        seo: "seo",
        privacy: "privacy",
        terms: "terms",
        testimonial: "pages",
        pages: "pages",
      } as Record<string, string>)[tabId ?? ""];
      if (control) {
        navigate(websiteEditorControlDestination(sectionByTab[tabId ?? ""] ?? "set-up", control));
        return;
      }
      navigate(websiteEditorDestination(sectionByTab[tabId ?? ""] ?? "set-up"));
      return;
    }
    if (sectionId && findSection(sectionId)) {
      setActiveSectionState(sectionId);
    }
    setActiveTabState(tabId ?? null);
    setOpen(true);
  }, [navigate]);

  const closeSettings = useCallback(() => {
    setOpen(false);
    setPendingGuard(null);
  }, []);

  const setActiveSection = useCallback((sectionId: string) => {
    if (sectionId === "client-billing") {
      setOpen(false);
      navigate(customerBillingSetupHref(window.location.search));
      return;
    }
    if (findSection(sectionId)) {
      setActiveSectionState(sectionId);
      // A new section always resolves to its own first tab.
      setActiveTabState(null);
    }
  }, [navigate]);

  const setActiveTab = useCallback((tabId: string) => {
    setActiveTabState(tabId);
  }, []);

  // Deep-link support: `?settings=<sectionId>` (+ optional `?settingsTab=<tabId>`)
  // from the sidebar Settings item, the Recommended Next Steps card, and the
  // retired per-page gears. Open the hub to that section/tab, then strip the
  // one-shot params so a refresh / back doesn't reopen it. Reacting to
  // searchString (not just mount) lets a link reopen settings while already on
  // the same page. `?settings=1` (legacy) maps to the default section.
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const raw = params.get("settings");
    if (!raw) return;
    const sectionId = raw === "1" ? DEFAULT_SECTION_ID : raw;
    // Legacy Calendar and Tasks page settings now open the Calendar tab in
    // Business Info. These values are tab aliases, not section ids.
    // Legacy redirect: Settings → Email and Settings → Text Messaging moved
    // to their current standalone destinations. These aliases run BEFORE the
    // findSection guard.
    if (sectionId === "email") {
      openSettings(SETTINGS_DESTINATIONS.domainEmail.sectionId);
      params.delete("settings");
      params.delete("settingsTab");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? "?" + rest : ""));
      return;
    }
    // Customer SMS and missed-call texting were retired. Consume old links
    // without opening a removed setup surface.
    if (sectionId === "missed-call" || (sectionId === "messaging" && params.get("settingsTab") === "missed-call")) {
      params.delete("settings");
      params.delete("settingsTab");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? "?" + rest : ""));
      return;
    }
    if (sectionId === "sms" || (sectionId === "messaging" && params.get("settingsTab") === "sms")) {
      params.delete("settings");
      params.delete("settingsTab");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? "?" + rest : ""));
      return;
    }
    // Legacy redirect: the Settings → Lead Capture section was removed
    // (July 2026). Its Digital Receptionist ("contact-form") tab lives on
    // Marketing → Digital Receptionist → Lead Capture; the other tabs moved
    // to the admin-only Playbooks page, so plain users just land on the
    // Receptionist page too. Runs BEFORE the findSection guard — the section
    // no longer exists in the registry.
    // Online Booking moved to Schedule → Booking (Task 2584).
    if (sectionId === "booking") {
      window.location.assign("/sessions");
      return;
    }
    if (sectionId === "lead-capture") {
      window.location.assign("/flows/digital-receptionist?section=lead-capture");
      return;
    }
    const requestedTab = params.get("settingsTab");
    const openAndConsume = (nextSection: string, nextTab?: string) => {
      params.delete("settings");
      params.delete("settingsTab");
      const rest = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (rest ? "?" + rest : ""),
      );
      // Consume the old modal link before navigating so its cleanup cannot
      // strip the new Website section from a redirected Footer destination.
      openSettings(nextSection, nextTab);
    };
    if (
      sectionId === "client-billing" || (sectionId === "messaging"
      && (!params.get("settingsTab") || params.get("settingsTab") === "client-billing"))
    ) {
      setOpen(false);
      navigate(customerBillingSetupHref(params.toString()), { replace: true });
      return;
    }
    if (sectionId === "messaging" && (requestedTab === "email" || requestedTab === "domain-email")) {
      openAndConsume(SETTINGS_DESTINATIONS.domainEmail.sectionId);
      return;
    }
    if (sectionId === "tasks" || sectionId === "calendar") {
      openAndConsume("business-info", "calendar");
      return;
    }
    // Import/Export moved from its own Settings section to tabs under Business
    // Info. The current combined tab exposes both tools.
    if (sectionId === "data") {
      openAndConsume("business-info", "data");
      return;
    }
    // Old affiliate bookmarks now open the ordinary account profile.
    if (sectionId === "affiliate") {
      openAndConsume("account-info", "profile");
      return;
    }
    // Dashboard now belongs under Settings → Business Info.
    if (sectionId === "dashboard") {
      openAndConsume("business-info", "dashboard");
      return;
    }
    // Logos and accent now belong to Website → Set Up.
    if (sectionId === "business-info" && (requestedTab === "logo" || requestedTab === "accent")) {
      setOpen(false);
      navigate(websiteEditorDestination("site-setup"), { replace: true });
      return;
    }
    if (sectionId === "business-info" && requestedTab === "footer-branding") {
      setOpen(false);
      navigate(websiteEditorDestination("footer"));
      return;
    }
    // Website editor controls left the Settings registry. Old links still
    // resolve to the corresponding tab in the reorganized Website workspace.
    if (sectionId === "website" || sectionId === "public-page") {
      if (requestedTab === "colors" || requestedTab === "branding") {
        openAndConsume("business-info", "basics");
        return;
      }
      const sectionByTab: Record<string, WebsiteEditorSection> = {
        effects: "footer",
        seo: "set-up",
        "footer-branding": "footer",
        hero: "hero",
        privacy: "pages",
        terms: "pages",
        testimonial: "pages",
        pages: "pages",
        hours: "footer",
        contact: "footer",
        social: "footer",
      };
      if (sectionId === "website" && requestedTab === "domain") {
        openAndConsume(SETTINGS_DESTINATIONS.domainEmail.sectionId);
        return;
      }
      setOpen(false);
      const control = ({
        privacy: "privacy",
        terms: "terms",
        testimonial: "pages",
        pages: "pages",
      } as Record<string, string>)[requestedTab ?? ""];
      if (control) {
        navigate(websiteEditorControlDestination(sectionByTab[requestedTab ?? ""] ?? "set-up", control));
        return;
      }
      navigate(websiteEditorDestination(sectionByTab[requestedTab ?? ""] ?? "set-up"));
      return;
    }
    if (!findSection(sectionId)) return;
    const tabId = params.get("settingsTab") ?? undefined;
    openAndConsume(sectionId, tabId);
  }, [searchString, openSettings, navigate]);

  const value = useMemo(
    () => ({
      open,
      activeSection,
      activeTab,
      openSettings,
      closeSettings,
      setActiveSection,
      setActiveTab,
      pendingGuardMessage,
      setPendingGuard,
    }),
    [
      open,
      activeSection,
      activeTab,
      openSettings,
      closeSettings,
      setActiveSection,
      setActiveTab,
      pendingGuardMessage,
    ],
  );

  return (
    <SettingsHubContext.Provider value={value}>
      {children}
    </SettingsHubContext.Provider>
  );
}
